import { useCallback, useEffect, useRef } from 'react';

/**
 * Plays a looping alarm sound while `active` is true.
 *
 * Strategy:
 *   1. Try to load /alarm.mp3 via the Web Audio API.
 *   2. If that fails (file missing in dev), fall back to a synthesised beep
 *      using an oscillator so the alarm is always audible.
 *
 * AudioContext is created lazily on the first user interaction to satisfy
 * browser autoplay policy. Call `prime()` on the first click/keydown in
 * your app to guarantee the context is ready.
 */
export function useAlarmSound(active: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<AudioBufferSourceNode | OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const bufferRef = useRef<AudioBuffer | null>(null);
  const loadedRef = useRef(false);
  const activeRef = useRef(active);
  activeRef.current = active;

  function getContext(): AudioContext {
    if (!ctxRef.current) {
      ctxRef.current = new AudioContext();
    }
    return ctxRef.current;
  }

  /** Call once on a user interaction to satisfy browser autoplay policy. */
  const prime = useCallback(() => {
    const ctx = getContext();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => undefined);
    }
  }, []);

  // Pre-load alarm.mp3 once
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    fetch('/alarm.mp3')
      .then((r) => {
        if (!r.ok) throw new Error('alarm.mp3 not found');
        return r.arrayBuffer();
      })
      .then((ab) => getContext().decodeAudioData(ab))
      .then((buf) => {
        bufferRef.current = buf;
      })
      .catch(() => {
        // alarm.mp3 unavailable — will use oscillator fallback
      });
  }, []);

  function startSound() {
    const ctx = getContext();
    if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);

    const gain = ctx.createGain();
    gain.gain.value = 0.7;
    gain.connect(ctx.destination);
    gainRef.current = gain;

    if (bufferRef.current) {
      // Play the loaded MP3 in a loop
      const src = ctx.createBufferSource();
      src.buffer = bufferRef.current;
      src.loop = true;
      src.connect(gain);
      src.start();
      sourceRef.current = src;
    } else {
      // Synthesised two-tone alarm
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.value = 880;
      osc.connect(gain);
      osc.start();

      // Pulse the frequency between 880Hz and 440Hz at ~2Hz
      let high = true;
      const interval = setInterval(() => {
        if (!sourceRef.current) { clearInterval(interval); return; }
        (sourceRef.current as OscillatorNode).frequency.value = high ? 440 : 880;
        high = !high;
      }, 500);

      sourceRef.current = osc;
    }
  }

  function stopSound() {
    try {
      sourceRef.current?.stop?.();
    } catch {
      // already stopped
    }
    sourceRef.current = null;
    gainRef.current?.disconnect();
    gainRef.current = null;
  }

  useEffect(() => {
    if (active) {
      startSound();
    } else {
      stopSound();
    }
    return () => {
      if (!activeRef.current) return;
      stopSound();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  return { prime };
}
